import { CURRICULUM, WORDS, getLesson } from '../content/curriculum';
import type { AppState, Entitlement, LessonPlan, MasteryEvidenceType, ReviewQueueItem, ReviewReason, Skill, WordItem, WordProgress } from '../types';
import { applyCalendarSnapshot, calendarSnapshot, localDateKey } from './calendar';

const intervals = [1, 3, 7, 14, 30, 60, 120, 180];
const DAY_MS = 86_400_000;

export const rankTable = [
  ['Starter', 0], ['Learner', 400], ['Explorer', 1200], ['Speaker', 2800],
  ['Communicator', 5200], ['Fluent', 9000], ['Advanced', 14500], ['Master', 22000],
  ['Elite', 32000], ['Legend', 45000],
] as const;

export function todayKey(date = new Date(), timeZone = 'Asia/Samarkand') {
  return localDateKey(date, timeZone);
}

export function addDaysIso(days: number, from = new Date()) {
  const d = new Date(from);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString();
}

export function daysBetween(a: string, b: string) {
  const aa = new Date(a).getTime();
  const bb = new Date(b).getTime();
  return Math.floor((bb - aa) / DAY_MS);
}

export function getEntitlement(state: AppState): Entitlement {
  const now = Date.now();

  // ==========================================================
  // PRO
  // ==========================================================
  // proUntil client tomonidan yaratilmasligi kerak.
  // Bu qiymat server / SysOne License Hub orqali kelgan
  // entitlement cache sifatida ishlatiladi.
  if (
    state.profile.proUntil &&
    new Date(state.profile.proUntil).getTime() > now
  ) {
    return {
      plan: 'pro',
      active: true,
      status: 'active',
      proUntil: state.profile.proUntil,
      source: 'entitlement',
    };
  }

  // ==========================================================
  // 7 KUNLIK TRIAL
  // ==========================================================
  // Trial faqat:
  // 1) onboarding serverda yakunlangan bo'lsa;
  // 2) server trialStartedAt qiymatini bergan bo'lsa
  // mavjud hisoblanadi.
  //
  // Client hech qachon Date.now() bilan o'ziga yangi trial
  // yaratmaydi yoki eski trialni uzaytirmaydi.
  const trialStartedAt =
    state.profile.onboardingComplete &&
    state.profile.trialStartedAt
      ? state.profile.trialStartedAt
      : null;

  if (trialStartedAt) {
    const trialStartMs = new Date(trialStartedAt).getTime();

    if (Number.isFinite(trialStartMs)) {
      const trialEndMs = trialStartMs + 7 * DAY_MS;
      const trialEndsAt = new Date(trialEndMs).toISOString();

      if (trialEndMs > now) {
        return {
          plan: 'trial',
          active: true,
          status: 'active',
          trialEndsAt,
        };
      }

      return {
        plan: 'free',
        active: true,
        status: 'active',
        trialEndsAt,
      };
    }
  }

  // ==========================================================
  // FREE
  // ==========================================================
  // Onboarding tugamagan yoki server trial bermagan account.
  return {
    plan: 'free',
    active: true,
    status: 'active',
  };
}

export function hasProAccess(state: AppState) {
  const e = getEntitlement(state);
  return e.plan === 'trial' || e.plan === 'pro';
}

export function canAccess(state: AppState, feature: string) {
  if (feature === 'vocabulary' || feature === 'basic_srs') return true;
  return hasProAccess(state);
}

export function rankForXp(xp: number) {
  let rank: string = rankTable[0][0];
  for (const [name, threshold] of rankTable) if (xp >= threshold) rank = name;
  return rank;
}

function average(values: number[]) {
  return values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : 0;
}

export function knowledgeHealth(state: AppState) {
  const values = Object.values(state.progress.words);
  const vocabulary = values.length ? Math.round(average(values.map((w) => w.mastery))) : 0;
  const grammarValues = Object.values(state.progress.grammarMastery);
  const grammar = grammarValues.length ? Math.round(average(grammarValues)) : 0;
  const deepCandidates = values.filter((w) => (w.seen ?? 0) >= 2);
  const deepRecall = deepCandidates.length
    ? Math.round(average(deepCandidates.map((w) => Math.min(100, (w.strength ?? w.mastery) * (w.retrievability ?? 70) / 100))))
    : 0;
  return {
    vocabulary,
    grammar,
    listening: state.progress.listeningMastery,
    reading: state.progress.readingMastery,
    pronunciation: state.progress.pronunciationMastery,
    deepRecall,
  };
}

export function interfaceMix(state: AppState) {
  const accuracy = state.progress.totalAnswers
    ? state.progress.correctAnswers / state.progress.totalAnswers
    : 0;
  const health = knowledgeHealth(state);
  const skillMean = average([health.vocabulary, health.grammar, health.listening, health.reading, health.pronunciation]);
  const score = Math.min(100, Math.round(skillMean * 0.58 + accuracy * 34 + Math.min(8, state.progress.masteryStreak * 0.6)));
  if (state.progress.totalAnswers < 10 || score < 18) return { stage: 1, uz: 100, en: 0 };
  if (score < 38) return { stage: 2, uz: 70, en: 30 };
  if (score < 58) return { stage: 3, uz: 50, en: 50 };
  if (score < 78) return { stage: 4, uz: 20, en: 80 };
  return { stage: 5, uz: 0, en: 100 };
}

export function uiLabel(state: AppState, uz: string, en: string) {
  const mix = interfaceMix(state);
  if (mix.stage <= 1) return uz;
  if (mix.stage === 2) return `${uz} · ${en}`;
  if (mix.stage === 3) return `${en} · ${uz}`;
  return en;
}

function wordAgeDays(progress: WordProgress, date = new Date()) {
  const anchor = progress.lastRecalledAt || progress.lastSeenAt;
  if (!anchor) return 0;
  return Math.max(0, (date.getTime() - new Date(anchor).getTime()) / DAY_MS);
}

export function currentRetrievability(progress: WordProgress, date = new Date()) {
  if (!progress.seen) return 0;
  const stability = Math.max(0.75, progress.stabilityDays ?? 1);
  const age = wordAgeDays(progress, date);
  // Smooth forgetting estimate used only for scheduling. It avoids pretending that a stored
  // retrievability value remains unchanged for days between study sessions.
  return Math.max(0, Math.min(100, Math.round(100 * Math.exp(-age / stability))));
}

function reasonForProgress(progress: WordProgress, date = new Date()): ReviewReason | null {
  if (!progress.seen) return null;
  const retrievability = currentRetrievability(progress, date);
  const age = wordAgeDays(progress, date);
  if ((progress.failedRecalls ?? progress.wrong) >= 2 && (progress.mastery < 55 || retrievability < 45)) return 'rebuild';
  if (progress.mastery < 45) return 'weak';
  if ((progress.nextReviewAt && new Date(progress.nextReviewAt).getTime() <= date.getTime()) || retrievability <= 75) return 'due';
  if (age > 60) return 'deep_archive';
  if (age >= 15) return 'active_memory';
  return null;
}

export function reviewQueue(state: AppState, date = new Date()): ReviewQueueItem[] {
  const byId = new Map(WORDS.map((w) => [w.id, w]));
  const items: ReviewQueueItem[] = [];
  for (const progress of Object.values(state.progress.words)) {
    const word = byId.get(progress.wordId);
    if (!word) continue;
    const reason = reasonForProgress(progress, date);
    if (!reason) continue;
    const reasonBoost: Record<ReviewReason, number> = {
      rebuild: 80,
      due: 65,
      weak: 55,
      active_memory: 35,
      deep_archive: 45,
      boss: 20,
    };
    const retentionGap = Math.max(0, 100 - currentRetrievability(progress, date));
    const priority = reasonBoost[reason] + Math.max(0, 100 - progress.mastery) + Math.round(retentionGap * 0.45) + Math.min(30, progress.wrong * 4);
    items.push({ word, reason, priority });
  }
  return items.sort((a, b) => b.priority - a.priority || a.word.id.localeCompare(b.word.id));
}

export function dueWordIds(state: AppState, date = new Date()) {
  return reviewQueue(state, date).map((item) => item.word.id);
}

export function dueWords(state: AppState): WordItem[] {
  return reviewQueue(state).map((item) => item.word);
}

function uniqueWords(words: WordItem[]) {
  const seen = new Set<string>();
  return words.filter((word) => {
    if (seen.has(word.id)) return false;
    seen.add(word.id);
    return true;
  });
}

function wordsFromPastDays(currentDay: number, minOffset: number, maxOffset: number, take: number) {
  const candidates: WordItem[] = [];
  if (currentDay <= minOffset) return candidates;
  const span = Math.max(1, maxOffset - minOffset + 1);
  for (let i = 0; i < Math.min(span, 18); i += 1) {
    const offset = minOffset + ((currentDay * 7 + i * 11) % span);
    const day = currentDay - offset;
    if (day < 1) continue;
    const lesson = getLesson(day);
    const word = lesson.newWords[(currentDay + i) % lesson.newWords.length];
    if (word) candidates.push(word);
    if (uniqueWords(candidates).length >= take) break;
  }
  return uniqueWords(candidates).slice(0, take);
}

function dateKeyForCourseDay(startedOn: string, day: number) {
  const [year, month, date] = startedOn.split('-').map(Number);
  if (!year || !month || !date || day < 1) return '';
  const shifted = new Date(Date.UTC(year, month - 1, date + day - 1));
  return shifted.toISOString().slice(0, 10);
}

export function missedCourseDays(state: AppState) {
  const snapshot = calendarSnapshot(state);
  const startedOn = state.profile.courseStartedOn;
  if (!startedOn || snapshot.day <= 1) return [] as number[];
  const completed = new Set(state.progress.dailyCompletionDates ?? []);
  const missed: number[] = [];
  for (let day = 1; day < snapshot.day && day <= 365; day += 1) {
    const key = dateKeyForCourseDay(startedOn, day);
    if (key && !completed.has(key)) missed.push(day);
  }
  return missed;
}

function recoveryWords(state: AppState, take = 4) {
  const missed = missedCourseDays(state).slice(-14).reverse();
  const seen = new Set(Object.keys(state.progress.words));
  const candidates: WordItem[] = [];
  // First recover unseen knowledge from missed calendar days, then previously seen weak material.
  for (const missedDay of missed) {
    const lesson = getLesson(missedDay);
    candidates.push(...lesson.newWords.filter((word) => !seen.has(word.id)));
    if (uniqueWords(candidates).length >= take) break;
  }
  if (uniqueWords(candidates).length < take) {
    for (const missedDay of missed) {
      candidates.push(...getLesson(missedDay).newWords);
      if (uniqueWords(candidates).length >= take) break;
    }
  }
  return uniqueWords(candidates).slice(0, take);
}

export function currentReviewDebt(state: AppState) {
  const queueDebt = reviewQueue(state).filter((item) => ['due', 'weak', 'rebuild'].includes(item.reason)).length;
  return Math.max(state.progress.reviewDebt ?? 0, queueDebt);
}

export function dynamicLesson(state: AppState): LessonPlan {
  const day = calendarSnapshot(state).day;
  const base = getLesson(day);
  const queue = reviewQueue(state);
  const debt = currentReviewDebt(state);
  const rebuild = queue.filter((item) => item.reason === 'rebuild').slice(0, 3).map((item) => item.word);
  const due = queue.filter((item) => item.reason === 'due').slice(0, 4).map((item) => item.word);
  const weak = queue.filter((item) => item.reason === 'weak').slice(0, 2).map((item) => item.word);
  const near = wordsFromPastDays(day, 1, 14, 2);
  const active = wordsFromPastDays(day, 15, 60, 2);
  const deep = wordsFromPastDays(day, 61, Math.max(61, Math.min(300, day - 1)), 2);
  const recovery = recoveryWords(state, state.progress.missedDays > 0 ? 4 : 0);

  const seenIds = new Set(Object.keys(state.progress.words));
  const unseenFromBase = base.newWords.filter((word) => !seenIds.has(word.id));
  const unseenGlobal = WORDS.filter((word) => !seenIds.has(word.id));
  const baseNewLimit = debt >= 30 ? 0 : debt >= 16 ? 1 : day <= 60 ? 4 : day <= 180 ? 3 : 2;
  const newLimit = state.progress.missedDays >= 7
    ? 0
    : state.progress.missedDays > 0
      ? Math.min(2, baseNewLimit)
      : baseNewLimit;
  const newWords = uniqueWords([...unseenFromBase, ...unseenGlobal]).slice(0, newLimit);
  const reviewWords = uniqueWords([
    ...recovery,
    ...rebuild,
    ...due,
    ...weak,
    ...near,
    ...active,
    ...deep,
    ...base.reviewWords,
    ...base.newWords.filter((word) => !newWords.some((item) => item.id === word.id)),
  ]).filter((word) => !newWords.some((newWord) => newWord.id === word.id)).slice(0, 12);

  return {
    ...base,
    day,
    newWords,
    reviewWords,
    recoveryMode: debt >= 16 || Boolean(state.progress.recoveryMode) || state.progress.missedDays > 0,
    memoryMix: {
      due: due.length,
      weak: weak.length,
      active: active.length,
      deep: deep.length,
      rebuild: rebuild.length,
      recovery: recovery.length,
    },
  };
}

export function deterministicShuffle<T>(values: T[], seed: string) {
  const result = [...values];
  let hash = 2166136261;
  for (const char of seed) {
    hash ^= char.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  for (let i = result.length - 1; i > 0; i -= 1) {
    hash ^= hash << 13; hash ^= hash >>> 17; hash ^= hash << 5;
    const j = Math.abs(hash) % (i + 1);
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

const evidenceWeights: Record<MasteryEvidenceType, number> = {
  meaning_recall: 0.25,
  form_recall: 0.25,
  listening: 0.15,
  context: 0.15,
  production: 0.20,
};

function evidenceMastery(progress: WordProgress) {
  const evidence = progress.evidence ?? {};
  return Math.round((Object.entries(evidenceWeights) as Array<[MasteryEvidenceType, number]>).reduce((sum, [kind, weight]) => {
    const stat = evidence[kind];
    if (!stat?.attempts) return sum;
    const accuracy = Math.max(0, Math.min(1, stat.correct / stat.attempts));
    const repetitionConfidence = Math.min(1, stat.attempts / 3);
    return sum + 100 * accuracy * weight * (0.55 + repetitionConfidence * 0.45);
  }, 0));
}

export function answerWord(
  state: AppState,
  word: WordItem,
  correct: boolean,
  latencyMs = 0,
  evidenceType: MasteryEvidenceType = 'meaning_recall',
): AppState {
  const now = new Date();
  const current: WordProgress = state.progress.words[word.id] ?? {
    wordId: word.id, seen: 0, correct: 0, wrong: 0, box: 0, mastery: 0,
  };
  const previousEvidence = current.evidence ?? (current.seen > 0 ? {
    meaning_recall: {
      attempts: current.seen,
      correct: current.correct,
      lastAt: current.lastSeenAt,
      bestLatencyMs: current.latencyMs,
    },
  } : {});
  const evidenceStat = previousEvidence[evidenceType] ?? { attempts: 0, correct: 0 };
  const evidence = {
    ...previousEvidence,
    [evidenceType]: {
      attempts: evidenceStat.attempts + 1,
      correct: evidenceStat.correct + (correct ? 1 : 0),
      lastAt: now.toISOString(),
      bestLatencyMs: latencyMs > 0
        ? Math.min(evidenceStat.bestLatencyMs ?? latencyMs, latencyMs)
        : evidenceStat.bestLatencyMs,
    },
  };

  const box = correct ? Math.min(intervals.length - 1, current.box + 1) : Math.max(0, current.box - 1);
  const correctCount = current.correct + (correct ? 1 : 0);
  const wrongCount = current.wrong + (correct ? 0 : 1);
  const seen = current.seen + 1;
  const failedRecalls = (current.failedRecalls ?? current.wrong) + (correct ? 0 : 1);
  const diversity = Math.min(12, Object.values(evidence).filter((item) => item?.attempts).length + Math.floor(correctCount / 4));
  const stabilityBase = current.stabilityDays ?? intervals[Math.max(0, current.box)] ?? 1;
  const stabilityDays = correct
    ? Math.max(1, Math.min(180, Math.round(stabilityBase * (1.35 + Math.min(0.8, correctCount * 0.04)))))
    : 1;
  const candidate: WordProgress = {
    ...current,
    seen,
    correct: correctCount,
    wrong: wrongCount,
    box,
    mastery: current.mastery,
    firstSeenAt: current.firstSeenAt ?? now.toISOString(),
    lastSeenAt: now.toISOString(),
    lastRecalledAt: correct ? now.toISOString() : current.lastRecalledAt,
    nextReviewAt: addDaysIso(correct ? stabilityDays : 1, now),
    strength: current.strength,
    stabilityDays,
    retrievability: correct ? 100 : 35,
    difficulty: Math.max(1, Math.min(100, Math.round((wrongCount / seen) * 100))),
    failedRecalls,
    contextDiversity: diversity,
    latencyMs: latencyMs > 0 ? latencyMs : current.latencyMs,
    reviewReason: !correct ? 'rebuild' : stabilityDays >= 60 ? 'deep_archive' : stabilityDays >= 15 ? 'active_memory' : 'due',
    evidence,
  };
  const mastery = Math.max(current.mastery && !current.evidence ? Math.round(current.mastery * 0.65) : 0, evidenceMastery(candidate));
  const updated = { ...candidate, mastery, strength: Math.min(100, Math.round(mastery * 0.75 + Math.min(25, stabilityDays / 4))) };
  const xpGain = correct ? (evidenceType === 'production' || evidenceType === 'form_recall' ? 10 : 7) : 0;
  const totalAnswers = state.progress.totalAnswers + 1;
  const correctAnswers = state.progress.correctAnswers + (correct ? 1 : 0);
  const xp = state.progress.xp + xpGain;
  const nextState: AppState = {
    ...state,
    progress: {
      ...state.progress,
      words: { ...state.progress.words, [word.id]: updated },
      totalAnswers,
      correctAnswers,
      xp,
      reviewDebt: Math.max(0, currentReviewDebt(state) + (correct ? -1 : 2)),
      rank: rankForXp(xp),
      skillAttempts: { ...state.progress.skillAttempts, vocabulary: (state.progress.skillAttempts.vocabulary ?? 0) + 1 },
    },
  };
  return applyCalendarSnapshot(nextState);
}

function skillValue(state: AppState, skill: Skill) {
  if (skill === 'listening') return state.progress.listeningMastery;
  if (skill === 'reading') return state.progress.readingMastery;
  if (skill === 'pronunciation') return state.progress.pronunciationMastery;
  if (skill === 'grammar') {
    const values = Object.values(state.progress.grammarMastery);
    return values.length ? Math.round(average(values)) : 0;
  }
  return knowledgeHealth(state).vocabulary;
}

export function answerSkill(state: AppState, skill: Exclude<Skill, 'vocabulary'>, correct: boolean, key = 'general'): AppState {
  // Grammar mastery is per grammar target. A new grammar topic must never inherit the
  // learner's average score from unrelated topics.
  const current = skill === 'grammar'
    ? (state.progress.grammarMastery[key] ?? 0)
    : skillValue(state, skill);
  const next = correct
    ? Math.min(100, Math.round(current + (100 - current) * 0.14))
    : Math.max(0, Math.round(current * 0.90));
  const xp = state.progress.xp + (correct ? 9 : 0);
  const progress = { ...state.progress, xp, rank: rankForXp(xp), skillAttempts: { ...state.progress.skillAttempts, [skill]: (state.progress.skillAttempts[skill] ?? 0) + 1 } };
  if (skill === 'grammar') progress.grammarMastery = { ...state.progress.grammarMastery, [key]: next };
  if (skill === 'listening') progress.listeningMastery = next;
  if (skill === 'reading') progress.readingMastery = next;
  if (skill === 'pronunciation') progress.pronunciationMastery = next;
  return applyCalendarSnapshot({ ...state, progress });
}

export function completeLesson(state: AppState, score: number) {
  const normalized = applyCalendarSnapshot(state);
  const snapshot = calendarSnapshot(normalized);
  const day = snapshot.day;
  const courseDate = snapshot.courseDate;
  const passed = score >= 75;
  const maintenance = snapshot.courseFinished;
  const alreadyCompleted = normalized.progress.dailyCompletionDates.includes(courseDate);
  const debtBefore = currentReviewDebt(normalized);
  const debtAfter = Math.max(0, passed ? debtBefore - 6 : debtBefore + 8);
  const masteryStreak = passed ? normalized.progress.masteryStreak + 1 : 0;
  const firstPassToday = !maintenance && passed && !alreadyCompleted;
  const xpGain = firstPassToday ? 40 + Math.round(score / 5) + (day % 7 === 0 ? 60 : 0) : 0;
  const xp = normalized.progress.xp + xpGain;
  const completedDays = firstPassToday && !normalized.progress.completedDays.includes(day)
    ? [...normalized.progress.completedDays, day]
    : normalized.progress.completedDays;
  const dailyCompletionDates = firstPassToday
    ? [...normalized.progress.dailyCompletionDates, courseDate]
    : normalized.progress.dailyCompletionDates;
  const lessonSessions = [...normalized.progress.lessonSessions, {
    day,
    courseDate,
    completedAt: new Date().toISOString(),
    score,
    passed,
    reviewDebtAfter: debtAfter,
  }].slice(-180);

  return applyCalendarSnapshot({
    ...normalized,
    progress: {
      ...normalized.progress,
      day,
      completedDays,
      dailyCompletionDates,
      masteryStreak,
      xp,
      rank: rankForXp(xp),
      lastStudyDate: passed ? courseDate : normalized.progress.lastStudyDate,
      reviewDebt: debtAfter,
      recoveryMode: !passed || debtAfter >= 16 || snapshot.missedDays > 0,
      failedLessonAttempts: normalized.progress.failedLessonAttempts + (passed ? 0 : 1),
      lessonSessions,
      lastMasteryScore: score,
      todayMissionCompleted: maintenance ? false : (alreadyCompleted || passed),
    },
  });
}

export function masteryGate(state: AppState) {
  const score = state.progress.lastMasteryScore ?? 0;
  const debt = currentReviewDebt(state);
  return {
    passed: score >= 75 && debt < 30,
    score,
    reviewDebt: debt,
    recoveryRequired: state.progress.recoveryMode || score < 75 || debt >= 16,
  };
}

export function curriculumProgress(state: AppState) {
  const day = calendarSnapshot(state).day;
  return Math.min(100, Math.round(((day - 1) / Math.max(1, CURRICULUM.length - 1)) * 100));
}

export function weeklyBossDay(day: number) {
  return day % 7 === 0 ? day : Math.min(365, day + (7 - (day % 7)));
}
